import {
  TmPhaseId,
  TimeMaterialPhaseRequest,
  TimeMaterialPhaseResponse,
  TimeMaterialItemsCanaResponse,
  TimeMaterialWmsDemandResponse,
} from './../../entities/tm/TimeMaterialResponse';
import {
  OrderActivityPageQuery,
  OrderEstimateId,
  OrderId,
  OrderProductId,
  OrderRequest,
  OrderStageId,
  OrderVendorId,
} from '@business/entities/order';
import { OrgId, JustAddressReponse, PaymentId } from '@business/entities/organization';
import { InvoiceId } from '@business/entities/TransactionInvoice';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { Endpoint } from '../endpoint';
import { Paginate } from './../../entities/common/Paginate';
import { OrderActivityResponse } from './../../entities/order/OrderActivityResponse';
import {
  EstimateLineItem,
  OrderCancelRequest,
  OrderEmailTemplateRequest,
  OrderShippingRequest,
  OrderWithItemsResposne,
  ReceiveItemRequest,
  LaborReportRecords,
} from './../../entities/order/OrderResponse';
import { OrderShipmentRequest, OrderShipmentStatus } from './../../entities/order/OrderShipmentResponse';
import { PoType, PurchaseOrderId } from './../../entities/order/PurchaseOrderResponse';
import {
  CompactTimeMaterialResponse,
  TimeMaterialResponse,
  TimeMaterialStatus,
  OrderEstimateResponse,
  TimeMaterialId,
  ProductStatus,
} from '@business/entities/tm';
import { sendEmailTimeTrackingRequest, TimeLogResponse } from '@business/entities/time_log';
import { UserId, CompactUserResponse } from '@business/entities/account';
import { FileResponse } from '@business/entities/file';
import { POStatusID } from '@business/entities/poStatus';
import { ProcessTmInvoiceFormData } from '@modules/organizations/OrganizationPage/Components/Billing/TimeMaterialPaymentModal/TimeMaterialPaymentModal';
import { QBSalesTaxResponse } from '@business/entities/salesTax';
import { GeneratePoProductFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/GeneratePoModal/GeneratePoModal';
import { CustomerStatementResponse } from '@business/entities/tm/CustomerStatementResponse';

export function fetchOrders(orgId?: OrgId) {
  return Endpoint.get<CompactTimeMaterialResponse[]>(`@gc/time-materials`, { params: { orgId } });
}
export function fetchOrgOrders(orgId?: OrgId) {
  return Endpoint.get<TimeMaterialResponse[]>(`@gc/time-materials/org`, { params: { orgId } });
}

export function searchOrders(search: string) {
  return Endpoint.get<TimeMaterialResponse[]>(`@gc/time-materials/search`, { params: { search } });
}

export function selectManager(orderId: OrderId, manager: UserId) {
  return Endpoint.patch<CompactUserResponse>(`@gc/time-materials/select-manager/${orderId}`, { data: { manager } });
}

export function selectCustomerServiceRep(orderId: OrderId, customerServiceRep: UserId) {
  return Endpoint.patch<CompactUserResponse>(`@gc/time-materials/select-customer-service-rep/${orderId}`, { data: { customerServiceRep } });
}

export function updateUsers(orderId: TimeMaterialId, users: string[]) {
  return Endpoint.post<string[]>(`@gc/time-materials/update-users/${orderId}`, { data: { users } });
}

export function fetchOrder(id: OrderId, isInvoice?: boolean, isPublic = false, withLogo = false) {
  if (isPublic) {
    return Endpoint.get<TimeMaterialResponse>(`@gc/public/time-materials/order/${id}`, { params: { isInvoice, withLogo } });
  }
  return Endpoint.get<TimeMaterialResponse>(`@gc/time-materials/order/${id}`, { params: { isInvoice, withLogo } });
}

export function fetchCustomerStatement(id: TimeMaterialId) {
  return Endpoint.get<CustomerStatementResponse>(`@gc/public/time-materials/customer-statement/${id}`);
}

export function downloadCustomerStatement(id: TimeMaterialId) {
  return Endpoint.get<void>(`@gc/public/time-materials/customer-statement-download/${id}`, {
    isFile: true,
  });
}

export function createOrder(data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials`, { formData, hasFiles: true });
}

export function updateOrder(id: OrderId, data: Partial<OrderRequest>, isSend = false) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/time-materials/${id}`, { data: { ...data, isSend } });
}

export function updateEstimate(id: OrderId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/estimate/${id}`, { formData, hasFiles: true });
}

export function updateInvoice(id: InvoiceId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-material-invoice/${id}`, { formData, hasFiles: true });
}

export function createInvoice(id: OrderId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-material-invoice/${id}`, { formData, hasFiles: true });
}

export function deleteOrder(id: OrderProductId) {
  return Endpoint.delete<void>(`@gc/time-materials/${id}`);
}
export function acceptEstimate(id: OrderEstimateId, name: string, email: string) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/accepted/${id}`, { data: { name, email } });
}
export function createOrderInvoice(id: OrderEstimateId, invoiceId: InvoiceId, sent: boolean, data?: Partial<OrderRequest>) {
  const formData = CommonService.toFormData(data);
  formData.append('sent', sent ? 'true' : 'false');
  formData.append('invoiceId', invoiceId);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-material-invoice/send/${id}`, { formData, hasFiles: true });
}

export function sendEstimate(id: OrderEstimateId, data?: Partial<OrderRequest>) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/estimate-send/${id}`, { data });
}

export function updateStage(
  id: OrderId,
  stage: OrderStageId,
  deliveryDate?: number,
  closeIt = false,
  sendIt?: boolean,
  emailData?: OrderEmailTemplateRequest,
) {
  const formData = CommonService.toFormData({ ...emailData, stage, deliveryDate, closeIt, sendIt });
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/stage/${id}`, { formData, hasFiles: true });
}

export function updateStatus(id: OrderId, status: TimeMaterialStatus, reason?: string, isSend?: boolean, data?: OrderCancelRequest) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/status/${id}`, { data: { ...data, status, isSend } });
}

export function updateShipping(id: OrderId, stage: OrderStageId, data: OrderShippingRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipping/${id}/${stage}`, { formData, hasFiles: true });
}

export function rejectEstimate(id: OrderEstimateId, reason: string) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/rejected/${id}`, { data: { reason } });
}
export function sendPurchaseOrder(id: OrderId, vendorId: OrderVendorId, data: Partial<OrderEmailTemplateRequest>, poId: string) {
  const formData = CommonService.toFormData(data);
  formData.append('vendorId', vendorId || '0');
  formData.append('purchaseOrderId', poId || '0');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/vendor-order/${id}`, { formData, hasFiles: true });
}

export function createPurchaseOrder(id: OrderId, vendorId?: OrderVendorId, address?: JustAddressReponse) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/po-create/${id}`, { data: { vendorId, address } });
}

export function closePurchaseOrder(id: PurchaseOrderId) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/purchase-close/${id}`, { data: {} });
}
export function download(orderId: OrderId, vendorId: OrderVendorId, filename: string) {
  return Endpoint.get<void>(`@gc/time-materials/po-download/${orderId}/${vendorId}`, { isFile: true, filename });
}

export function downloadPdf(purchaseOrderId: PurchaseOrderId, filename: string, type: PoType) {
  return Endpoint.get<void>(`@gc/time-materials/purchase-order-download/${purchaseOrderId}`, { isFile: true, filename, params: { type } });
}

export function downloadLading(id: PurchaseOrderId, filename: string) {
  return Endpoint.get<void>(`@gc/purchase-orders/lading/${id}`, { isFile: true, filename });
}

export function downloadPickList(id: PurchaseOrderId, filename: string) {
  return Endpoint.get<void>(`@gc/purchase-orders/pick-list/${id}`, { isFile: true, filename });
}

export function purchaseOrderStatus(poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/orders/po-status/${poId}`, { data: { ...data, status } });
}

export function updatePoStatus(poId: PurchaseOrderId, status: POStatusID) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/orders/update-po-status/${poId}`, { data: { status } });
}

export function updatePurchaseOrder(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/purchase-orders/${id}`, { formData, hasFiles: true });
}

export function saveBillOfLading(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/purchase-orders/pick-list/${id}`, { data });
}

export function savePickList(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/purchase-orders/pick-list/${id}`, { data });
}

export function createShipment(poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, status, isSend });
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipment/${poId}`, { formData, hasFiles: true });
}

export function createShipmentWithOutPO(id: TimeMaterialId, data: Partial<OrderShipmentRequest>, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, isSend });
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipment-without-po/${id}`, { formData, hasFiles: true });
}

export function fetchActivities(options: OrderActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<OrderActivityResponse[]>(`@gc/time-material-activity`, { params });
}

export function fetchPaginateActivities(options: OrderActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<Paginate<OrderActivityResponse>>(`@gc/time-material-activity`, { params });
}

export function updateShipment(id: OrderId, data: OrderShippingRequest, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, isSend });
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipment/${id}`, { formData, hasFiles: true });
}

export function deleteShipment(id: OrderId) {
  return Endpoint.delete<TimeMaterialResponse>(`@gc/time-materials/shipment/${id}`);
}

export function deleteInvoice(invoiceId: InvoiceId, orderId: OrderId) {
  return Endpoint.delete<TimeMaterialResponse>(`@gc/orders/invoice/${invoiceId}`, { data: { orderId } });
}

export function openedEstimate(id: OrderEstimateId) {
  return Endpoint.post<void>(`@gc/orders/estimate-opened/${id}`);
}

export function searchOrderItems(search: string) {
  return Endpoint.get<OrderWithItemsResposne[]>(`@gc/time-materials/search-order`, { params: { search } });
}

export function updateEstimateLineItem(id: number, data: Partial<EstimateLineItem>) {
  return Endpoint.patch<OrderEstimateResponse>(`@gc/time-materials/estimate-item/${id}`, { data });
}

export function updateItemsStatus(orderId: string, status: ProductStatus, itemIds: number[], date?: number) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/estimate-item-status/${orderId}`, { data: { status, itemIds, date } });
}

export function updateBulkItemsStatus(
  orderId: string,
  status: ProductStatus,
  items: Array<{ itemId: number; installedQty: number }>,
  installedDate: number,
) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/estimate-item-installed-qty/${orderId}`, { data: { status, items, installedDate } });
}

export function getOrderCanaStatus(id: OrderId) {
  return Endpoint.get<TimeMaterialItemsCanaResponse>(`@gc/time-materials/cana-items-status/${id}`);
}

export function getWmsDemand(id: OrderId) {
  return Endpoint.get<TimeMaterialWmsDemandResponse[]>(`@gc/time-materials/requested-wms-demands/${id}`);
}

export function unRequest(orderId: OrderId, id: string) {
  return Endpoint.post<TimeMaterialWmsDemandResponse>(`@gc/time-materials/un-request-requested-parts/${orderId}/${id}`);
}

// Time Material Phases
export function fetchPhases(timeMaterialId: TimeMaterialId) {
  return Endpoint.get<TimeMaterialPhaseResponse[]>(`@gc/time-material/phase/${timeMaterialId}`);
}
export function fetchPhase(phaseId: TmPhaseId) {
  return Endpoint.get<TimeMaterialPhaseResponse>(`@gc/time-material/phase/${phaseId}`);
}
export function createPhase(timeMaterialId: TimeMaterialId, data: Partial<TimeMaterialPhaseRequest>) {
  return Endpoint.post<TimeMaterialPhaseResponse>(`@gc/time-material/phase/${timeMaterialId}`, { data });
}
export function updatePhase(phaseId: TmPhaseId, data: Partial<TimeMaterialPhaseRequest>) {
  return Endpoint.patch<TimeMaterialPhaseResponse>(`@gc/time-material/phase/${phaseId}`, { data });
}
export function updatePhaseDate(phaseId: TmPhaseId, date: number) {
  return Endpoint.post<TimeMaterialPhaseResponse>(`@gc/time-material/phase/update-date/${phaseId}`, { data: { date: date } });
}
export function deletePhase(phaseId: TmPhaseId) {
  return Endpoint.delete<void>(`@gc/time-material/phase/${phaseId}`);
}
export function sortPhases(ids: number[]) {
  return Endpoint.post<void>(`@gc/time-material/phase/sort`, { data: { ids } });
}
export function updatePhaseUsers(phaseId: TmPhaseId, users: string[]) {
  return Endpoint.post<string[]>(`@gc/time-material/phase/update-users/${phaseId}`, { data: { users } });
}

export function orderItems(timeMaterialId: TimeMaterialId, items: any[]) {
  return Endpoint.post<TimeMaterialPhaseResponse[]>(`@gc/time-material/phase/sort-items/${timeMaterialId}`, { data: { items } });
}
export function receiveItems(id: OrderId, data: ReceiveItemRequest[]) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/receive-items/${id}`, { data: { data } });
}
export function getReceiveItems(id: OrderId) {
  return Endpoint.get<ReceiveItemRequest[]>(`@gc/time-materials/receive-items/${id}`);
}
export function getTimeTrackingReport(id: OrderId) {
  return Endpoint.get<LaborReportRecords[]>(`@gc/time-materials/time-tracking-report/${id}`);
}

export function downloadTimeTrackingReport(id: OrderId) {
  return Endpoint.get<void>(`@gc/time-materials/download-time-tracking-report/${id}`, { isFile: true });
}

export function downloadProgressReportReport(id: OrderId) {
  return Endpoint.get<void>(`@gc/time-materials/download-project-progress-report/${id}`, { isFile: true });
}

export function sendEmail(orderId: string, data: sendEmailTimeTrackingRequest) {
  return Endpoint.post<TimeLogResponse>(`@gc/time-materials/send-email/${orderId}`, { data });
}

export function getFiles(id: OrderId, isPublic = false) {
  return Endpoint.get<FileResponse[]>(`${isPublic ? `@gc/public/time-materials/files/${id}` : `@gc/time-materials/files/${id}`}`);
}

export function updateDates(orderId: OrderId, data: Partial<CompactTimeMaterialResponse>) {
  return Endpoint.patch<CompactTimeMaterialResponse>(`@gc/time-materials/dates/${orderId}`, { data });
}

export function processTmInvoicePayment(id: TimeMaterialId, paymentId: PaymentId, amount: number, data: ProcessTmInvoiceFormData) {
  const allData = { ...data, amount, paymentId } as any;
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/process-invoice-payment/${id}`, { data: allData });
}

export function getSalesTaxOptions() {
  return Endpoint.get<QBSalesTaxResponse[]>(`@gc/time-materials/sales-tax-options`);
}

export function createBulkPurchaseOrder(id: OrderId, address: JustAddressReponse, data: GeneratePoProductFormData) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/po-generate/${id}`, { data: { address, data } });
}

export function moveItems(phaseId: TmPhaseId, ids: string[]) {
  return Endpoint.post<TimeMaterialPhaseResponse[]>(`@gc/time-material/phase/move-items/${phaseId}`, { data: { ids } });
}

export function sendCustomerStatement(orderId: TimeMaterialId) {
  return Endpoint.post<void>(`@gc/time-materials/send-customer-statement/${orderId}`);
}
// Time Material Phases
